/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import debounce from 'lodash.debounce'
import { useCreateOrUpdateDocumentRowValueWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useGetRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import { DocumentRowValue } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import generateUuid from '@/utils/uuid'
import { useDocumentCurrentStateViewContext } from '@/components/image-zoom-pan/providers/DocumentCurrentStateViewProvider'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import { useDocumentRowsContext } from './providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from './providers/DocumentRowValuesProvider'
import {
  buildFieldIdentifier,
  FieldIdentifier,
  useDocumentsChangeSetsContext,
} from './providers/DocumentsChangeSetsProvider'
import { useFieldErrorContext } from './providers/FieldErrorProvider'
import { getRowValuesMainValues } from './useGetValueData'

export type DataLineageValues = {
  original_value: string | null
  rule_value: string | null
  manual_value: string | null
  final_value: string | null
}

type UseFieldValueProps = {
  field?: ProjectGridField
  documentRowId?: string
}

type UseFieldValueReturn = {
  value: string
  dataLineageValues: DataLineageValues
  hasManualValue: boolean
  refId: string | null
  jsonValue: Record<string, any> | null
  onManualChange: (
    inputField: string | null,
    refId?: string | null,
    jsonObject?: Record<string, any> | null,
  ) => void
  onCategoryChange: (newCategoryId: string | null) => void
  clearValue: () => void
  documentRowValue?: DocumentRowValue
  wasAutoFilled: boolean
  metadata: Record<string, any>
}

const NULL_INTERNAL_VALUE = {
  value: null,
  refId: null,
  jsonValue: null,
}

export default function useFieldValue({
  field,
  documentRowId,
}: UseFieldValueProps): UseFieldValueReturn {
  const { document } = useDocumentContext()
  const { baseRow } = useDocumentRowsContext()
  const fieldId = field?.id || ''
  // documentRow from context is second because there might be one for the base grid,
  // so we don't want to override the value passed for fields in table
  const rowId = documentRowId || baseRow?.id || null
  const { setFieldError } = useFieldErrorContext()
  const { queryKey, getFieldDocumentRowValue } = useDocumentRowValuesContext()
  const { documentCurrentStateView } = useDocumentCurrentStateViewContext()
  const documentsChangeSetsContextData = useDocumentsChangeSetsContext()
  const { markFieldsUpdating, isFieldUpdating, clearFieldsBeingUpdated } =
    documentsChangeSetsContextData

  const documentRowValue = getFieldDocumentRowValue(fieldId, rowId)
  const {
    manual_value = null,
    rule_value = null,
    original_value = null,
    final_value = null,
    final_ref_id,
    final_value_json = null,
    project_content_category_item_id,
  } = documentRowValue ? documentRowValue : ({} as DocumentRowValue)

  const [internal, setInternal] = useState<{
    value: string | null
    refId: string | null
    jsonValue: Record<string, any> | null
  }>(NULL_INTERNAL_VALUE)

  const fieldIsBeingUpdated = useMemo(
    () => isFieldUpdating(fieldId, rowId),
    [fieldId, rowId, isFieldUpdating],
  )

  const wasFieldUpdatingRef = useRef(false)

  useEffect(() => {
    if (fieldIsBeingUpdated) {
      wasFieldUpdatingRef.current = true
      return
    }

    if (wasFieldUpdatingRef.current) {
      wasFieldUpdatingRef.current = false
      setInternal(NULL_INTERNAL_VALUE)
    }
  }, [fieldIsBeingUpdated])

  const { selectedWorkflowState } = useSelectedWorkflowStateContext()
  const getRelevantWorkflowState = useGetRelevantWorkflowState()
  const state = selectedWorkflowState || getRelevantWorkflowState(document)

  const { createOrUpdateDocumentRowValue } =
    useCreateOrUpdateDocumentRowValueWithDocChange({
      documentId: document?.id,
      workflowId: state?.workflow_id || '',
      workflowStateId: state?.id || '',
      listQueryKey: queryKey,
      documentsChangeSetsContextData,
      onError: () => {
        clearFieldsBeingUpdated([buildFieldIdentifier(fieldId, rowId)])
        setInternal(NULL_INTERNAL_VALUE)
        setFieldError(fieldId, rowId, 'Unable to update field.')
      },
    })

  const createOrUpdateDocumentRowValueRef = useRef(
    createOrUpdateDocumentRowValue,
  )

  const updateValue = useCallback(
    function (
      newValues: {
        manual_value?: string | null
        project_content_category_item_id?: string | null
      },
      newRefId?: string | null,
      newJsonObject?: Record<string, any> | null,
    ) {
      const updatedValues = {
        manual_value: newValues.manual_value ?? null,
        project_content_category_item_id:
          newValues.project_content_category_item_id || null,
        manual_ref_id: newRefId || null,
        manual_value_json: newJsonObject || null,
      }

      const valueId = documentRowValue?.id || generateUuid()
      const updatedDocumentRowValue = {
        ...documentRowValue,
        id: valueId,
        project_grid_field_id: fieldId,
        document_row_id: rowId,
        ...updatedValues,
      } as DocumentRowValue
      if (!documentRowValue) {
        return createOrUpdateDocumentRowValueRef.current(
          updatedDocumentRowValue,
          'new',
        )
      }

      return createOrUpdateDocumentRowValueRef.current(
        updatedDocumentRowValue,
        'update',
      )
    },
    [documentRowValue, fieldId, rowId],
  )

  // If we switch documents, update the ref
  useEffect(() => {
    createOrUpdateDocumentRowValueRef.current = createOrUpdateDocumentRowValue
  }, [document?.id, createOrUpdateDocumentRowValue])

  const debouncedHandleChange = useMemo(
    () =>
      debounce((newValue, newRefId, newJsonObject = null) => {
        setFieldError(fieldId, rowId, '')
        updateValue({ manual_value: newValue }, newRefId, newJsonObject)
      }, 500),
    [fieldId, rowId, setFieldError, updateValue],
  )

  const onCategoryChange = useCallback(
    (newCategoryId: string | null) => {
      setInternal(NULL_INTERNAL_VALUE)
      setFieldError(fieldId, rowId, '')
      updateValue({
        project_content_category_item_id: newCategoryId,
        manual_value: null, // retroactively clean out manual values, since they shouldn't be set for category pickers
      })
    },
    [rowId, fieldId, setFieldError, updateValue],
  )

  const setManualChangeAsFieldUpdating = useCallback(() => {
    if (!fieldId || !rowId) return

    const fieldIdentifier: FieldIdentifier = buildFieldIdentifier(
      fieldId,
      rowId,
    )
    markFieldsUpdating(
      [fieldIdentifier],
      field?.project_grid_field_type.code === 'live-list-picker',
    )
  }, [field?.project_grid_field_type.code, fieldId, markFieldsUpdating, rowId])

  const onManualChange = useCallback(
    (
      newValue: string | null,
      newManualRefId?: string | null,
      jsonObject?: Record<string, any> | null,
    ) => {
      const isNoneOption =
        newValue === 'None' &&
        (newManualRefId === 'None' || jsonObject?.entity_id === 'None')
      setInternal((prev) => {
        const next = {
          value: isNoneOption ? '' : newValue,
          refId:
            newManualRefId !== undefined
              ? isNoneOption
                ? null
                : newManualRefId
              : prev.refId,
          jsonValue:
            jsonObject !== undefined
              ? isNoneOption
                ? null
                : jsonObject
              : prev.jsonValue,
        }
        return next
      })

      if (isNoneOption) {
        setFieldError(fieldId, rowId, '')
        updateValue({ manual_value: '' }, null, null)
      } else if (newValue !== null) {
        debouncedHandleChange(newValue as string, newManualRefId, jsonObject)
      } else {
        // When deleting or clearing the manual value, we want it to happen instantly
        setFieldError(fieldId, rowId, '')
        updateValue({}, newManualRefId, null)
      }
      setManualChangeAsFieldUpdating()
    },
    [
      setManualChangeAsFieldUpdating,
      setFieldError,
      fieldId,
      rowId,
      updateValue,
      debouncedHandleChange,
    ],
  )

  const clearValue = useCallback(() => {
    setInternal(NULL_INTERNAL_VALUE)
    setFieldError(fieldId, rowId, '')
    if (manual_value !== null) {
      updateValue({})
      setManualChangeAsFieldUpdating()
    }
  }, [
    setFieldError,
    fieldId,
    rowId,
    manual_value,
    updateValue,
    setManualChangeAsFieldUpdating,
  ])

  const rowValueMainData = getRowValuesMainValues(documentRowValue)

  return {
    value:
      documentCurrentStateView === 'validation'
        ? internal.value ??
          rowValueMainData.value ??
          project_content_category_item_id ??
          ''
        : final_value ?? '',
    hasManualValue: manual_value !== null,
    jsonValue:
      documentCurrentStateView === 'validation'
        ? internal.jsonValue ?? rowValueMainData.jsonValue
        : final_value_json,
    refId:
      documentCurrentStateView === 'validation'
        ? internal.refId ?? rowValueMainData.refId
        : final_ref_id,
    dataLineageValues: {
      original_value,
      rule_value,
      manual_value,
      final_value,
    },
    onManualChange,
    onCategoryChange,
    clearValue,
    documentRowValue,
    wasAutoFilled: !!rule_value && !original_value && !manual_value,
    metadata: documentRowValue?.metadata || {},
  }
}
